import { Typography } from '@mui/material'
import { FeatureFlagIdAccessReviewsESignature, FeatureFlagIdReviewsPendingActions } from 'c1gen/features'
import { C1Alert, C1AlertTitle, C1LoadingButton } from 'c1ui'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { usePathname } from 'next/navigation'
import { AccessReviewServiceGetRequest } from 'pbts/c1/api/accessreview/v1/accessreview'
import { GetPendingActionsByTaskIDsRequest, InitiateStepUpForSubmitRequest, SubmitReviewRequest } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { FC, useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getAccessReviewApi } from 'redux_toolkit/access-reviews'
import { getPendingActionsByTaskIdsApi, initiateStepUpForSubmitApi, submitReviewApi } from 'redux_toolkit/access_review/pending_actions'
import { PendingActionsStateV2, selectConsolidatedPendingActionState } from 'redux_toolkit/slices/pendingActionsSliceV2'

interface TaskPendingActionBannerProps {
    task: TaskValue
}

export const TaskPendingActionBanner: FC<TaskPendingActionBannerProps> = ({ task }) => {
    const intl = useIntl()
    const toast = useAlert()
    const hasPendingActionsFeature = useFeatureChecker(FeatureFlagIdReviewsPendingActions)
    const hasESignatureFF = useFeatureChecker(FeatureFlagIdAccessReviewsESignature) ?? false
    const accessReviewId = task.accessReview?.id
    const pathname = usePathname()

    // Read consolidated pending action state (includes optimistic updates from PendingTicketActionTracker)
    const consolidatedPendingActionState = useSelector(
        (state: { pendingActionsV2: PendingActionsStateV2 }) => selectConsolidatedPendingActionState(state, task.id),
    )

    // All hooks must be called before any early returns
    const { data: response } = getPendingActionsByTaskIdsApi.useGetPendingActionsByTaskIDs(
        GetPendingActionsByTaskIDsRequest.create({
            accessReviewId: accessReviewId || '',
            certifyTaskIds: [task.id],
        }),
        { actionable: !!accessReviewId, evictionAfterSeconds: 0 },
    )

    // Fetch access review to check signature requirement
    const {
        data: accessReview,
        isLoading: isLoadingAccessReview,
    } = getAccessReviewApi.useGet(
        AccessReviewServiceGetRequest.create({
            id: accessReviewId || '',
            expandMask: { paths: ['*'] },
        }),
        { actionable: !!accessReviewId },
    )

    const requiresSignature = (hasESignatureFF && accessReview?.signatureConfig?.requireSignature) ?? false

    const { mutate: submit, inProgress: isSubmitting } = submitReviewApi()
    const { mutate: initiateStepUp, inProgress: isStepUpInProgress } = initiateStepUpForSubmitApi()
    const invalidatePendingActions = getPendingActionsByTaskIdsApi.useInvalidateGetPendingActionsByTaskIDs()

    // Determine pending action from consolidated state (includes optimistic updates)
    // or fall back to API response for backwards compatibility
    const pendingAction = useMemo(() => {
        // Prefer consolidated state from pendingActionsSliceV2 (includes optimistic updates)
        if (consolidatedPendingActionState?.state.oneofKind === 'unlocked') {
            return consolidatedPendingActionState.state.unlocked
        }

        // Fall back to direct API response if consolidated state not available
        const apiPendingState = response?.states?.[task.id]
        if (apiPendingState?.state.oneofKind === 'unlocked') {
            return apiPendingState.state.unlocked
        }

        return undefined
    }, [consolidatedPendingActionState, response, task.id])

    const handleSubmit = useCallback(async (): Promise<void> => {
        if (!accessReviewId) return

        try {
            if (requiresSignature) {
                // Use step-up flow for signature-required submissions
                const stepUpState = await initiateStepUp(
                    InitiateStepUpForSubmitRequest.create({
                        accessReviewId,
                        certifyTaskId: task.id,
                        returnPath: pathname,
                    }),
                )

                const stepUpResponse = stepUpState.data
                if (!stepUpResponse) return

                // Redirect to authorization URL if available
                const responseWithFields = stepUpResponse as { authorizationUrl?: string }
                if (responseWithFields.authorizationUrl) {
                    window.location.href = responseWithFields.authorizationUrl
                } else {
                    toast(
                        intl.formatMessage({
                            id: 'pending_actions_step_up_error',
                            defaultMessage: 'Error initiating step-up authentication',
                        }),
                        'error',
                    )
                }
            } else {
                // Direct submission for non-signature cases
                const submitState = await submit(
                    SubmitReviewRequest.create({
                        accessReviewId,
                        certifyTaskId: task.id,
                        acknowledgeConflicts: false,
                    }),
                )

                const submitResponse = submitState.data
                if (!submitResponse) return

                if (submitResponse.submissionAccepted?.submissionId) {
                    toast(
                        intl.formatMessage({
                            id: 'pending_actions_submitted',
                            defaultMessage: 'Review submitted successfully',
                        }),
                        'success',
                    )
                    invalidatePendingActions()
                }
            }
        } catch (err) {
            toast(
                intl.formatMessage({
                    id: 'pending_actions_submit_error',
                    defaultMessage: 'Error submitting review',
                }),
                'error',
            )
        }
    }, [accessReviewId, task.id, requiresSignature, initiateStepUp, submit, invalidatePendingActions, toast, intl, pathname])

    // Early returns after all hooks are called
    if (!hasPendingActionsFeature || !accessReviewId || !pendingAction) return null

    const isLoading = isLoadingAccessReview || isSubmitting || isStepUpInProgress

    return (
        <C1Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
                <C1LoadingButton variant="contained" onClick={handleSubmit} loading={isLoading}>
                    <FormattedMessage id="pending_actions_submit_review" defaultMessage="Submit" />
                </C1LoadingButton>
            }
        >
            <C1AlertTitle>
                <FormattedMessage
                    id="task_pending_action_banner_title"
                    defaultMessage="Awaiting submission"
                />
            </C1AlertTitle>
            <Typography variant="body2">
                <FormattedMessage
                    id="task_pending_action_queued"
                    defaultMessage="Submitting will confirm your decision on this review"
                />
            </Typography>
        </C1Alert>
    )
}
